/**
 * Budget Summary Panel — C2 Budget & Cost Lite
 *
 * Shows planned versus actual, and the hours × rate estimate separately.
 * A missing actual stays "Not recorded". Hidden for guests.
 */

import { useEffect, useState } from 'react';
import { DollarSign } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { canSeeCost } from '@/utils/access';

interface CostSummary {
  projectId: string;
  budgetAmount: number | null;
  currency: string;
  rate: number;
  costTrackingEnabled: boolean;
  plannedHours: number;
  actualHours: number;
  remainingHours: number;
  plannedCost: number;
  actualCost: number | null;
  estimatedLabourCost: number | null;
  costVariance: number;
  forecastAtCompletion: number;
}

function unwrap<T>(res: any): T {
  return res?.data?.data ?? res?.data ?? res;
}

function fmt(n: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}

function varianceText(value: number | null, currency: string): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return fmt(value, currency);
}

function money(value: number | null | undefined, currency = 'USD'): string {
  if (value == null || Number.isNaN(Number(value))) return 'Not recorded';
  return fmt(Number(value), currency);
}

export function BudgetSummaryPanel({ projectId }: { projectId: string }) {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const { user } = useAuth();
  // Phase 2A fix: use canSeeCost instead of broken isGuest from useWorkspaceRole
  const costVisible = canSeeCost(null, user?.platformRole ?? user?.role);
  const [data, setData] = useState<CostSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId || !costVisible) {
      setLoading(false);
      return;
    }
    apiClient
      .get(`/work/projects/${projectId}/cost-summary`, {
        headers: { 'x-workspace-id': activeWorkspaceId },
      })
      .then((res) => setData(unwrap<CostSummary>(res)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId, activeWorkspaceId, costVisible]);

  if (!costVisible || loading || !data) return null;

  const currency = data.currency || 'USD';
  const planned = data.budgetAmount;
  const actual = data.actualCost ?? null;
  const variance =
    planned != null && actual != null ? planned - actual : null;
  const labor =
    data.costTrackingEnabled && data.estimatedLabourCost != null
      ? data.estimatedLabourCost
      : null;

  return (
    <div
      className="rounded-lg border bg-white p-4 space-y-3"
      data-testid="budget-summary-panel"
    >
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-900">Budget</h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded border p-2">
          <p className="text-[10px] text-gray-500 uppercase">Planned</p>
          <p className="text-base font-bold text-gray-900">{money(planned, currency)}</p>
        </div>
        <div className="rounded border p-2">
          <p className="text-[10px] text-gray-500 uppercase">Actual</p>
          <p className="text-base font-bold text-gray-900">{money(actual, currency)}</p>
        </div>
        <div className="rounded border p-2">
          <p className="text-[10px] text-gray-500 uppercase">Variance</p>
          <p className="text-base font-bold text-gray-900" data-testid="budget-variance">{varianceText(variance, currency)}</p>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Estimated labor cost (hours × rate) {money(labor, currency)}
      </p>
    </div>
  );
}
